import React, { useState } from 'react';
import { EcrionProvider, Utils } from 'ecrion-components';
import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';
import { Redirect } from 'react-router';
import { useUser } from './context/UserContext';
import './App.css';
import { Route, Switch } from 'react-router-dom';
import SignIn from './components/SignIn';
import Home from './components/Home';
import MyAccount from './components/MyAccount';
import PublicLink from './components/PublicLink';
import File from './components/File';
import OnDemand from './components/OnDemand';
import PageNotFound from './components/PageNotFound';
import ResetPasswordForm from './components/SignIn/ResetPasswordForm';
import dataTypes from './data';
import theme from './theme';
import ErrorPage from './components/ErrorPage';

const history = createBrowserHistory();
const localStorageKey = 'ECRION.SERVER.URL';

function App() {
  const [shouldRender, setShouldRender] = useState(false);

  const query = new URLSearchParams(window.location.search);
  const publicId = query.get('publicId');
  const resetPasswordToken = query.get('reset');
  const context = query.get('context');
  const user = useUser();

  React.useEffect(() => {
    let isMounted = true;
    /* When the current user has the same login name as the userName parameter from the url, remove the parameter*/
    const newUserName = new URLSearchParams(window.location.search).get(
      'userName'
    );
    const sameUser = user && newUserName && newUserName === user.LoginName;

    if (user && sameUser)
      history.push(
        window.location.pathname +
        removeSearchParameter(window.location.search, 'userName') +
        window.location.hash
      );

    const token = window.localStorage.getItem('ECR.EOS.AccessToken');
    const serverURL = window.localStorage.getItem('ECRION.SERVER.URL');
    const headers = {};
    if (token) {
      headers.Authorization = `Basic ${btoa(token)}`;
    }

    if (user) {
      if (isMounted) setShouldRender(false);

      Utils.request({
        method: 'GET',
        url: `${serverURL}/backend/content?workspace=System&path=Portal/Themes/CustomStyle.css`,
        headers: headers,
      })
        .then((res) => {
          const head =
            document.head || document.getElementsByTagName('head')[0];
          const style = document.createElement('style');
          style.textContent = res;
          head.append(style);

          if (isMounted) setShouldRender(true);
        })
        .catch(() => {
          //render without custom css
          if (isMounted) setShouldRender(true);
        });
    }

    if (!user && isMounted) setShouldRender(true);

    return () => {
      if (shouldRender === false) setShouldRender(true);
      isMounted = false;
    };
  }, [user]);

  React.useEffect(() => {
    let serverURL = '';
    if (process.env.NODE_ENV === 'development')
      serverURL = process.env.REACT_APP_SERVER_URL;
    else {
      serverURL = `${window.location.protocol}//${window.location.hostname}`;
      const port = window.location.port;
      if (port) {
        serverURL = serverURL.concat(`:${port}`);
      }
    }
    if (!user) setShouldRender(true);
    window.localStorage.setItem(localStorageKey, serverURL);
  }, []);

  return (
    <>
      {shouldRender && (
        <Router history={history}>
          <EcrionProvider
            routes={{ history, RedirectComponent: Redirect }}
            theme={theme}
            dataTypes={dataTypes}
            disableAuthRedirect
          >
            {user ? (
              <Switch>
                <Route path="/:environment/publiclink" component={PublicLink} />
                <Route path="/:environment/file" component={File} />
                <Route path="/:environment/ondemand" component={OnDemand} />
                <Route path="/:environment/profile" component={MyAccount} />
                <Route path="/:environment/error" component={ErrorPage} />
                <Route path="/:environment/:pinId?" component={Home} />
                <Route exact path={`/`} component={Home} />
                <Route path="*" component={PageNotFound} />
                {/* TODO: in case of a login route redirect to root */}
              </Switch>
            ) : (
              <Switch>
                <Route path="/:environment/publiclink" component={PublicLink} />
                {publicId && (
                  <Route path="/:environment/OnDemand" component={OnDemand} />
                )}
                {resetPasswordToken && (
                  <Route
                    path="/:environment"
                    render={(props) => (
                      <ResetPasswordForm
                        resetToken={resetPasswordToken}
                        context={context}
                        {...props}
                      />
                    )}
                  />
                )}
                <Route path="/:environment/error" component={ErrorPage} />
                <Route path="/:environment" component={SignIn} />
                <Route path="/" component={SignIn} />
              </Switch>
            )}
          </EcrionProvider>
        </Router>
      )}
    </>
  );
}

function removeSearchParameter(searchQuery, parameter) {
  if (!searchQuery) return '';
  const searchString = searchQuery.substring(1);
  const params = searchString.split(/[&;]/g);
  const paramPrefix = parameter + '=';

  for (let i = 0; i < params.length; i++) {
    if (params[i].startsWith(paramPrefix)) {
      params.splice(i, 1);
    }
  }

  return params.length > 0 ? '?' + params.join('&') : '';
}

export default App;
